import React, { useState, useEffect, useRef } from 'react'
import { HiX, HiChevronLeft, HiChevronRight, HiZoomIn, HiZoomOut } from 'react-icons/hi'

const ImageCarousel = ({ 
  images = [], 
  isOpen, 
  onClose, 
  initialIndex = 0,
  title = "Image Preview"
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const [imageZoom, setImageZoom] = useState(100)
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 })
  const hoverTimeoutRef = useRef(null)

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex)
      setImageZoom(100)
      setImagePosition({ x: 0, y: 0 })
    }
  }, [isOpen, initialIndex])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
      }
    }
  }, [])

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return
      
      switch (e.key) {
        case 'Escape':
          onClose()
          break
        case 'ArrowLeft':
          e.preventDefault()
          goToPrevious()
          break
        case 'ArrowRight':
          e.preventDefault()
          goToNext()
          break
        case '+':
        case '=':
          e.preventDefault()
          handleZoomIn()
          break
        case '-':
          e.preventDefault()
          handleZoomOut()
          break
        default:
          break
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
    // goToNext/goToPrevious are declared below this effect, so naming them here
    // would evaluate them in the temporal dead zone and throw. They are safe to
    // omit: both advance via functional setState rather than reading
    // currentIndex, and images.length is already a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentIndex, images.length])

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1))
    setImageZoom(100)
    setImagePosition({ x: 0, y: 0 })
  }

  const goToNext = () => {
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0))
    setImageZoom(100)
    setImagePosition({ x: 0, y: 0 })
  }

  const goToIndex = (index) => {
    setCurrentIndex(index)
    setImageZoom(100)
    setImagePosition({ x: 0, y: 0 })
  }

  const handleThumbnailHover = (index) => {
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
    }
    
    // Set a small delay to prevent rapid switching while moving mouse across thumbnails
    hoverTimeoutRef.current = setTimeout(() => {
      goToIndex(index)
    }, 150) // 150ms delay for smooth hover experience
  }

  const handleThumbnailLeave = () => {
    // Clear timeout when leaving thumbnail area
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
    }
  }

  const handleZoomIn = () => {
    setImageZoom((prev) => Math.min(prev + 25, 400))
  }

  const handleZoomOut = () => {
    setImageZoom((prev) => Math.max(prev - 25, 25))
  }

  const handleMouseDown = (e) => {
    setIsDragging(true)
    setDragStart({
      x: e.clientX - imagePosition.x,
      y: e.clientY - imagePosition.y
    })
  }

  const handleMouseMove = (e) => {
    if (!isDragging) return
    
    setImagePosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    })
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  const handleWheel = (e) => {
    e.preventDefault()
    if (e.deltaY < 0) {
      handleZoomIn()
    } else {
      handleZoomOut()
    }
  }

  if (!isOpen || images.length === 0) return null

  const currentImage = images[currentIndex]

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
      <div className="relative w-full h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 bg-black bg-opacity-50 text-white">
          <div className="flex items-center space-x-4">
            <h3 className="text-lg font-semibold">{title}</h3>
            {images.length > 1 && (
              <span className="text-sm text-gray-300">
                {currentIndex + 1} of {images.length}
              </span>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            {/* Zoom Controls */}
            <div className="flex items-center space-x-1 bg-black bg-opacity-50 rounded-lg px-2 py-1">
              <button
                onClick={handleZoomOut}
                className="p-1 hover:bg-white hover:bg-opacity-20 rounded transition-colors"
                title="Zoom Out"
              >
                <HiZoomOut className="h-4 w-4" />
              </button>
              <span className="px-2 py-1 text-sm text-gray-300 min-w-[3rem] text-center">
                {imageZoom}%
              </span>
              <button
                onClick={handleZoomIn}
                className="p-1 hover:bg-white hover:bg-opacity-20 rounded transition-colors"
                title="Zoom In"
              >
                <HiZoomIn className="h-4 w-4" />
              </button>
            </div>
            
            <button
              onClick={onClose}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded transition-colors"
              title="Close"
            >
              <HiX className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex items-center justify-center relative">
          {/* Navigation Arrows */}
          {images.length > 1 && (
            <>
              <button
                onClick={goToPrevious}
                className="absolute left-4 p-2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-full transition-all z-10"
                title="Previous Image"
              >
                <HiChevronLeft className="h-6 w-6" />
              </button>
              
              <button
                onClick={goToNext}
                className="absolute right-4 p-2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-full transition-all z-10"
                title="Next Image"
              >
                <HiChevronRight className="h-6 w-6" />
              </button>
            </>
          )}

          {/* Image Container */}
          <div 
            className="flex items-center justify-center w-full h-full overflow-hidden"
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onWheel={handleWheel}
          >
            <img
              src={currentImage.url || currentImage}
              alt={currentImage.alt || `Image ${currentIndex + 1}`}
              className={`max-w-full max-h-full object-contain rounded-lg select-none ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
              style={{ 
                transform: `scale(${imageZoom / 100}) translate(${imagePosition.x}px, ${imagePosition.y}px)`,
                transition: isDragging ? 'none' : 'transform 0.2s ease-in-out'
              }}
              onMouseDown={handleMouseDown}
              draggable={false}
            />
          </div>
        </div>

        {/* Thumbnail Navigation */}
        {images.length > 1 && (
          <div className="p-4 bg-black bg-opacity-50">
            <div className="flex justify-center space-x-2 overflow-x-auto">
              {images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => goToIndex(index)}
                  onMouseEnter={() => handleThumbnailHover(index)}
                  onMouseLeave={handleThumbnailLeave}
                  className={`flex-shrink-0 w-16 h-16 rounded-lg border-2 transition-all ${
                    index === currentIndex 
                      ? 'border-white' 
                      : 'border-transparent hover:border-gray-400'
                  }`}
                  title={`View image ${index + 1} (click or hover)`}
                >
                  <img
                    src={image.url || image}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover rounded-lg"
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ImageCarousel
